import React from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';

export interface ChatSourceNoteProps {
  /** Opens the trail: the sources queried, the queries, calculations and charts. */
  onOpen: () => void;
  /** The sentence before the link. Defaults to how an answer was built. */
  children?: React.ReactNode;
  /** The link's words. */
  label?: string;
}

/**
 * A quiet line under an answer saying where it came from, with a link to the
 * whole trail — the agent's own aside rather than a button among buttons.
 *
 * Show it only where there is a trail to open; an answer that queried nothing
 * has nothing to show.
 */
export function ChatSourceNote({
  onOpen,
  children = 'This reply was built from your connected data and workspace steps.',
  label = 'View sources and calculation trail',
}: ChatSourceNoteProps) {
  return (
    <Typography
      component="p"
      variant="caption"
      sx={(t) => ({ m: 0, color: t.tokens.text.secondary, lineHeight: 1.55, maxWidth: 520 })}
    >
      {children}{' '}
      <Tooltip title="See which sources were queried, the SQL or API requests, calculations and charts." enterDelay={350}>
        <Link
          component="button"
          type="button"
          variant="inherit"
          onClick={onOpen}
          // The tooltip would otherwise become the link's name, leaving a
          // screen reader with the explanation and not the action.
          aria-label={label}
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            verticalAlign: 'text-bottom',
            p: 0,
            border: 'none',
            background: 'none',
            font: 'inherit',
            fontWeight: 600,
            cursor: 'pointer',
            color: t.tokens.text.accent,
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            '&:hover': { color: t.tokens.brand.primaryHover },
            '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
          })}
        >
          <TimelineOutlined fontSize="small" aria-hidden />
          <Box component="span">{label}</Box>
        </Link>
      </Tooltip>
    </Typography>
  );
}
